// Official prior-session daily bars, shared only within one account workspace.
// No process-global account cache and no quote/forming-candle subscription.
import { createContext, createElement, useCallback, useContext, useMemo, useState, useSyncExternalStore } from 'react'
import { toCandles } from '../lib/candleStore.js'
import { fetchIbkrChartHistory } from '../lib/marketData/ibkrChartAdapter.js'

const IDLE = Object.freeze({ bars: [], status: 'idle' })
const DailyPivotContext = createContext(null)

export function needsDailyPivotSource(indicators, mode, isOption) {
  return mode === 'live' && !isOption && indicators.some(ind =>
    ind.kind === 'pivots' && ind.style?.visible !== false && (ind.params?.bar_size || '1 day') === '1 day')
}

function createSource() {
  const entries = new Map()
  const requestScope = crypto.randomUUID()
  let requestGeneration = 0
  return {
    snapshot: symbol => entries.get(symbol)?.snapshot || IDLE,
    subscribe(symbol, listener) {
      if (!symbol) return () => {}
      let entry = entries.get(symbol)
      if (!entry) {
        entry = { listeners: new Set(), controller: new AbortController(), snapshot: { bars: [], status: 'loading' } }
        entries.set(symbol, entry)
        const publish = snapshot => {
          if (entry.controller.signal.aborted || entries.get(symbol) !== entry) return
          entry.snapshot = snapshot
          entry.listeners.forEach(notify => notify())
        }
        fetchIbkrChartHistory({ symbol, timeframe: 'daily', session: 'regular' }, {
          purpose: 'daily-pivots', signal: entry.controller.signal,
          requestScope, requestGeneration: ++requestGeneration,
        }).then(raw => publish({ bars: Array.isArray(raw) ? raw : [], status: 'ready' }),
          () => publish({ bars: [], status: 'error' }))
      }
      entry.listeners.add(listener)
      return () => {
        entry.listeners.delete(listener)
        if (!entry.listeners.size) {
          entry.controller.abort()
          entries.delete(symbol)
        }
      }
    },
  }
}

// WorkspaceCharts keys this provider by account owner, preventing reuse at logout.
export function DailyPivotSourceProvider({ children }) {
  const [source] = useState(createSource)
  return createElement(DailyPivotContext.Provider, { value: source }, children)
}

// Preserve the official session date before toCandles converts it to chart time.
function readIbkrDayKey(rawTime) {
  if (typeof rawTime === 'string' && /^\d{8}$/.test(rawTime)) return rawTime
  if (
    typeof rawTime === 'number'
    && Number.isFinite(rawTime)
    && Number.isInteger(rawTime)
    && rawTime >= 19000101
    && rawTime <= 99991231
  ) {
    return String(rawTime)
  }
  return null
}

export function useDailyPivotSource(symbol, enabled, timezone) {
  const shared = useContext(DailyPivotContext)
  const [standalone] = useState(createSource)
  const source = shared || standalone
  const key = enabled && symbol ? symbol : null
  const subscribe = useCallback(listener => source.subscribe(key, listener), [source, key])
  const getSnapshot = useCallback(() => key ? source.snapshot(key) : IDLE, [source, key])
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => IDLE)
  const dailyBars = useMemo(() => {
    if (snapshot.status !== 'ready') return []
    return toCandles(snapshot.bars, timezone).map((bar, i) => {
      const ibkrDayKey = readIbkrDayKey(snapshot.bars[i]?.time)
      return ibkrDayKey ? { ...bar, ibkrDayKey } : bar
    })
  }, [snapshot, timezone])
  return { dailyBars, status: snapshot.status }
}
